import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CreateIcon from '@material-ui/icons/Create';
import React, { ChangeEvent, useState } from 'react';

interface RefundPolicyProps {
  refundPolicy: string | undefined;
  handleNewRefundPolicy: (refundPolicy: string) => void;
  canEdit: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(0),
    },
    refund: {
      maxHeight: '180px',
      width: '100%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: 'black',
    },
    viewEditButton: {
      position: 'absolute',
      right: theme.spacing(9),
      border: '1px solid #0000001A',
    },
    viewModal: {
      width: 960,
    },
    editModal: {
      width: 960,
      paddingBottom: 4,
    },
    editContent: {
      marginTop: theme.spacing(2),
    },
    editActions: {
      padding: theme.spacing(3),
    },
    editButtons: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    error: {
      padding: 0,
      backgroundColor: '#FFEFEE',
    },
    errorText: {
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      paddingTop: 4,
      paddingBottom: 4,
      color: '#FF3B30',
      fontWeight: 500,
    },
  }),
);

const RefundPolicy: React.FC<RefundPolicyProps> = props => {
  const classes = useStyles();
  const { refundPolicy, handleNewRefundPolicy, canEdit } = props;
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [value, setValue] = useState(refundPolicy);
  const [error, setError] = useState(false);

  const handleViewOpen = () => {
    setEditOpen(false);
    setViewOpen(true);
  };
  const handleViewClose = () => {
    setViewOpen(false);
  };
  const handleEditOpen = () => {
    setViewOpen(false);
    setEditOpen(true);
  };
  const handleEditClose = () => {
    setEditOpen(false);
    setValue(refundPolicy); //In case the user clicks escape
    setError(false);
  };
  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    if (value) {
      setError(false);
    }
  };
  const handleCancelClick = () => {
    setError(false);
    setEditOpen(false);
    setValue(refundPolicy);
  };
  const handleSaveClick = () => {
    if (!value) {
      setError(true);
    } else {
      handleNewRefundPolicy(value);
      setEditOpen(false);
    }
  };

  const viewRefundModal = (
    <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="md" data-cy="refund-policy-dialog">
      <DialogTitle className={classes.viewModal}>
        Refund Policy
        {canEdit && (
          <Button
            variant="outlined"
            className={classes.viewEditButton}
            color="primary"
            startIcon={<CreateIcon />}
            onClick={handleEditOpen}
            data-cy="dialog-edit-refund-policy"
          >
            EDIT
          </Button>
        )}
        <IconButton className={classes.closeButton} onClick={handleViewClose} data-cy="close-refund-policy-dialog">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography>{refundPolicy}</Typography>
      </DialogContent>
    </Dialog>
  );

  const editRefundModal = (
    <Dialog open={editOpen} onClose={handleEditClose} disableBackdropClick maxWidth="md">
      <DialogTitle className={classes.editModal}>Edit Refund Policy</DialogTitle>
      {error && (
        <DialogTitle className={classes.error}>
          <Typography className={classes.errorText} data-cy="error-message">
            To send payment requests, you must have a refund policy
          </Typography>
        </DialogTitle>
      )}
      <DialogContent className={classes.editContent}>
        <TextField
          value={value}
          onChange={handleValueChange}
          variant="outlined"
          multiline
          label="Refund Policy"
          fullWidth
          rows={33}
          placeholder="Write or paste your refund policy."
          data-cy="refund-policy"
        />
      </DialogContent>
      <DialogActions className={classes.editActions}>
        <Button variant="text" onClick={handleCancelClick} className={classes.editButtons} data-cy="cancel-refund-policy">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveClick}
          className={classes.editButtons}
          data-cy="save-refund-policy"
        >
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!refundPolicy) {
    if (!canEdit) {
      return <Typography variant="body2">No refund policy. An Editor or Admin must add a refund policy.</Typography>;
    }
    return (
      <>
        <Button variant="text" color="primary" onClick={handleEditOpen} size="small">
          Add Refund Policy
        </Button>
        {viewRefundModal}
        {editRefundModal}
      </>
    );
  }
  return (
    <>
      <Grid xs={12} item>
        <Typography variant="body2" className={classes.refund}>
          {refundPolicy}
        </Typography>
      </Grid>
      <Grid xs={12} item>
        <Button variant="text" color="primary" onClick={handleViewOpen} size="small" data-cy="view-full-policy">
          View Full Policy
        </Button>
        {canEdit && (
          <Button
            variant="text"
            color="primary"
            size="small"
            startIcon={<CreateIcon />}
            onClick={handleEditOpen}
            data-cy="edit-refund-policy"
          >
            Edit
          </Button>
        )}
      </Grid>
      {viewRefundModal}
      {editRefundModal}
    </>
  );
};

export default RefundPolicy;
